import { useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import Rights from './components/Rights';
import About from './components/About';
import Testimonials from './components/Testimonials';
import Process from './components/Process';
import CTA from './components/CTA';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import FloatingButtons from './components/FloatingButtons';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://barbaramarcelinoclaudino.com.br';

export default function Home() {
  useEffect(() => {
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'Bárbara Marcelino Claudino - Advogada Trabalhista',
        description: 'Advocacia trabalhista especializada em defesa de direitos do trabalhador em Juiz de Fora - MG.',
        inLanguage: 'pt-BR',
        publisher: {
          '@id': `${SITE_URL}/#organization`,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': ['LegalService', 'LocalBusiness'],
        '@id': `${SITE_URL}/#organization`,
        name: 'Bárbara Marcelino Claudino - Advocacia Trabalhista',
        alternateName: 'BMC Advocacia',
        description: 'Advocacia trabalhista especializada em defesa de direitos do trabalhador em Juiz de Fora - MG. Atendimento humanizado para casos de demissão injusta, horas extras, verbas rescisórias, assédio moral e mais.',
        url: SITE_URL,
        telephone: '+55-32-99856-6774',
        email: 'Barbaramarcelinoadv@hotmail.com',
        image: 'https://static.readdy.ai/image/c58860c0de9290e106b8a55542714277/073974e5b6d6dc4e6a4e834d20c0beaf.jpeg',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/c58860c0de9290e106b8a55542714277/073974e5b6d6dc4e6a4e834d20c0beaf.jpeg',
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Juiz de Fora',
          addressRegion: 'MG',
          postalCode: '36000-000',
          addressCountry: 'BR',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: -21.7642,
          longitude: -43.3503,
        },
        areaServed: [
          {
            '@type': 'City',
            name: 'Juiz de Fora',
          },
          {
            '@type': 'Country',
            name: 'Brasil',
          },
        ],
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '08:00',
            closes: '18:00',
          },
        ],
        sameAs: [
          'https://www.instagram.com/barbaramarcelinoadv',
        ],
        priceRange: 'Consulte',
        hasMap: 'https://maps.google.com/?q=-21.7642,-43.3503',
        knowsAbout: [
          'Direito do Trabalho',
          'Demissão Injusta',
          'Horas Extras',
          'Verbas Rescisórias',
          'Assédio Moral',
          'Direitos Trabalhistas',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Attorney',
        '@id': `${SITE_URL}/#attorney`,
        name: 'Bárbara Marcelino Claudino',
        jobTitle: 'Advogada Trabalhista',
        description: 'Advogada inscrita na OAB/MG sob o nº 225.351, especialista em direito e processo do trabalho.',
        url: SITE_URL,
        telephone: '+55-32-99856-6774',
        email: 'Barbaramarcelinoadv@hotmail.com',
        image: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/c58860c0de9290e106b8a55542714277/b1507c51408365ea6e58ffb4ede5b729.jpeg',
        },
        worksFor: {
          '@id': `${SITE_URL}/#organization`,
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Juiz de Fora',
          addressRegion: 'MG',
          addressCountry: 'BR',
        },
        sameAs: [
          'https://www.instagram.com/barbaramarcelinoadv',
        ],
        knowsAbout: [
          'Direito do Trabalho',
          'Processo do Trabalho',
          'Demissão Injusta',
          'Horas Extras',
          'Verbas Rescisórias',
          'Assédio Moral',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: 'Bárbara Marcelino Claudino | Advogada Trabalhista em Juiz de Fora MG',
        description: 'Advogada trabalhista em Juiz de Fora - MG. Defesa de direitos trabalhistas: demissão injusta, horas extras, verbas rescisórias, assédio moral. Atendimento humanizado em todo Brasil.',
        inLanguage: 'pt-BR',
        isPartOf: {
          '@id': `${SITE_URL}/#website`,
        },
        about: {
          '@id': `${SITE_URL}/#attorney`,
        },
        dateModified: '2025-07-14',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Início',
              item: SITE_URL,
            },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Quanto tempo tenho para reclamar meus direitos trabalhistas?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'O prazo prescricional para ações trabalhistas é de 2 anos após o término do contrato de trabalho, sendo possível reclamar direitos referentes aos últimos 5 anos de trabalho. É importante buscar orientação jurídica o quanto antes para não perder seus direitos.',
            },
          },
          {
            '@type': 'Question',
            name: 'Preciso ter sido demitido para buscar meus direitos?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Não necessariamente. Você pode buscar orientação jurídica mesmo estando empregado, especialmente em casos de irregularidades graves como assédio moral, condições insalubres não reconhecidas, ou não pagamento de horas extras. Cada situação deve ser analisada individualmente.',
            },
          },
          {
            '@type': 'Question',
            name: 'Como funciona o pagamento dos honorários advocatícios?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Os honorários advocatícios são definidos de acordo com a complexidade do caso, o trabalho envolvido e outros fatores. Essa conversa acontece de forma transparente durante a consulta inicial, respeitando sempre as normas éticas da OAB.',
            },
          },
          {
            '@type': 'Question',
            name: 'Quanto tempo dura um processo trabalhista?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'O tempo de duração varia conforme a complexidade do caso, a comarca, a necessidade de perícias e outros fatores. Processos mais simples podem ser resolvidos em alguns meses, enquanto casos complexos podem levar mais tempo.',
            },
          },
          {
            '@type': 'Question',
            name: 'Posso ter certeza de que vou ganhar a causa?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Não é ético nem possível garantir o resultado de qualquer ação judicial. O que posso oferecer é uma análise técnica honesta do seu caso, orientação sobre as possibilidades jurídicas e dedicação integral na defesa dos seus direitos.',
            },
          },
          {
            '@type': 'Question',
            name: 'Preciso ter documentos para iniciar uma consulta?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Documentos como carteira de trabalho, contracheques, rescisão, comprovantes de pagamento e mensagens relacionadas ao trabalho são muito úteis, mas não são obrigatórios para a primeira conversa.',
            },
          },
          {
            '@type': 'Question',
            name: 'O que é considerado assédio moral no trabalho?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Assédio moral caracteriza-se por condutas abusivas, repetitivas e prolongadas que atingem a dignidade ou integridade psíquica do trabalhador, colocando em risco seu emprego ou degradando o ambiente de trabalho.',
            },
          },
          {
            '@type': 'Question',
            name: 'Posso ser demitido durante o período de estabilidade?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Existem situações que garantem estabilidade provisória ao trabalhador, como gravidez, acidente de trabalho, membro da CIPA, entre outras. A demissão durante esses períodos pode ser considerada nula ou gerar direito a indenização.',
            },
          },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      const existing = document.getElementById(`schema-jsonld-${i}`);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = `schema-jsonld-${i}`;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, i) => {
        const el = document.getElementById(`schema-jsonld-${i}`);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Hero />
      <Services />
      <Rights />
      <About />
      <Testimonials />
      <Process />
      <CTA />
      <FAQ />
      <Footer />
      <FloatingButtons />
    </div>
  );
}
